import paths from '@constants/paths';
import useCart from '@hooks/useCart';
import useStore from '@hooks/useStore';
import { AddressData, Suggestion } from '@occ/api-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMostLikelyCartAddress } from '@api/interfaces/cartApi';
import logger from '@config/logger';
import { getAddressLineSuggestions } from '@api/interfaces/postalApi';
import { trackSearchSuggestions } from '@helpers/analyticsHelpers/trackDeliveryWidget';
import debounce from '@helpers/debounce';
import Config from '@config';
import { MIN_SEARCH_LENGTH } from './FullAddressSearch';
import { usePathname } from 'next/navigation';

export const useAddressSearch = () => {
  const { cart } = useCart();
  const { store } = useStore();
  const pathname = usePathname();
  const isCheckout = useMemo(() => pathname === paths.CHECKOUT, [pathname]);

  // Saved address from backend/cart
  const [savedCustomerAddress, setSavedCustomerAddress] = useState<AddressData | null>(null);
  // Derived from savedCustomerAddress, formatted to be show as suggestion
  const [suggestionForYou, setSuggestionForYou] = useState<Suggestion | undefined>(undefined);
  // Suggestions from API search
  const [searchSuggestions, setSearchSuggestions] = useState<Suggestion[]>([]);

  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Fetch saved address from cart or backend
  const fetchSavedAddress = useCallback(async () => {
    try {
      const response = await getMostLikelyCartAddress();
      if (response) {
        setSavedCustomerAddress(response);
      }
    } catch (e) {
      if (e instanceof Error) {
        logger.error({ error: 'Could not get customerAddresses', message: e.message });
      }
    }
  }, []);

  // If saved address is not in cart, fetch from backend if possible
  useEffect(() => {
    const cartDeliveryAddress = cart?.deliveryAddress;
    if (cartDeliveryAddress?.line1 && cartDeliveryAddress?.postalCode && cartDeliveryAddress?.town) {
      setSavedCustomerAddress(cartDeliveryAddress);
    } else {
      fetchSavedAddress();
    }
  }, [cart?.deliveryAddress, fetchSavedAddress]);

  // Derive suggestionForYou from savedCustomerAddress
  useEffect(() => {
    if (!savedCustomerAddress) return;

    setSuggestionForYou({
      street: savedCustomerAddress.line1,
      street_number: '',
      letter: '',
      postalcode: savedCustomerAddress.postalCode,
      locality: savedCustomerAddress.town,
    });
  }, [savedCustomerAddress]);

  // Fetch search suggestions (search results) from API
  const getSuggestions = async (query: string) => {
    try {
      const suggestions = await getAddressLineSuggestions(query.toLowerCase());
      setSearchSuggestions(suggestions.length > 0 ? suggestions : []);
      trackSearchSuggestions(true, suggestions, isCheckout, store?.storeId || '');
    } catch (e) {
      if (e instanceof Error) {
        logger.error({ error: 'Could not get suggestions', message: e?.message });
      }
      setSearchSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getSuggestionsRef = useRef(getSuggestions);

  useEffect(() => {
    getSuggestionsRef.current = getSuggestions;
  });

  // The "latest ref" pattern: getSuggestionsRef always points to the current
  // getSuggestions closure. The debounced function is created once and only reads
  // the ref when it fires (asynchronously, from an event handler) — never during render.
  // The linter can't prove this statically, hence the suppression.
  // eslint-disable-next-line react-hooks/refs
  const [debouncedGetSuggestions] = useState(() =>
    debounce((query: string) => {
      getSuggestionsRef.current(query);
    }, Config.TIMEOUT.addressSearchGetSuggestionsMs)
  );

  const clearSuggestions = () => {
    setSearchSuggestions([]);
    debouncedGetSuggestions.cancel();
    setIsLoading(false);
  };

  const onChange = (value: string) => {
    setInputValue(value);

    if (value.length >= MIN_SEARCH_LENGTH) {
      setIsLoading(true);
      debouncedGetSuggestions(value);
    } else {
      clearSuggestions();
    }
  };

  const onClear = () => {
    setInputValue('');
    clearSuggestions();
  };

  return {
    savedCustomerAddress,
    suggestionForYou,
    inputValue,
    searchSuggestions,
    isLoading,
    setInputValue,
    onChange,
    onClear,
  };
};
